import React from 'react';
import { TooltipToggleHost } from '../GameTip/CombatTooltip';
import { miniSubconcept } from '../../data/miniSubconcept';
import './EnemyRosterRail.css';

/**
 * EnemyRosterRail — the ONE canonical enemy-roster element for every combat
 * engine (batch cross-enemy swarm + simultaneous pools). It replaces the retired
 * SimultaneousCombat `sc-tiles` tile-grid, so the boss + minis read as a single
 * horizontal HP-bar rail everywhere.
 *
 * Presentational only. Each parent maps its own engine state into normalized
 * view-models and builds the per-enemy tooltip node (so the shared component
 * never imports either engine's tooltip/data helpers).
 *
 * enemy view-model:
 * {
 *   id,                       // React key + stable identity (required)
 *   name, tag,                // display label + node-tag text (BOSS / Red N / MINI / SPAWN)
 *   hp, maxHp,
 *   isBoss, isTarget, isDead, // state flags
 *   isInvuln, isSpawned, isAttacking,
 *   clickable,                // whether the bar is a focus-fire target
 *   slug?,                    // optional, surfaced as data-slug
 *   color?,                   // optional --effect-color accent for the tooltip
 *   tooltip?,                 // optional ready-to-render <CombatTooltipPanel/> node
 * }
 *
 * onSelect(vm) fires with the full view-model on click/enter/space — the parent
 * pulls whatever identity it targets by (index for batch, slug for simultaneous).
 *
 * embedded (Edward 2026-07-19) — PATTERN for any enemy tag rendered INSIDE another
 * element (a drug-toast warden preview, a status rail, etc.) rather than as a
 * primary combat roster. It drops the redundant per-enemy HP fraction (the X/Y
 * readout under the bar) — a static preview has no live HP to track, and the bar
 * fill already conveys it. Marks the strip `mini-boss-enemy-strip--embedded` so the
 * embedded treatment can grow. The primary combat roster (SimultaneousCombat) never
 * passes it, so its HP fraction stays.
 */
export default function EnemyRosterRail({ enemies = [], onSelect = null, className = '', embedded = false }) {
  const safe = Array.isArray(enemies) ? enemies : [];
  if (!safe.length) return null;

  return (
    <div
      className={['mini-boss-enemy-strip', embedded ? 'mini-boss-enemy-strip--embedded' : '', className].filter(Boolean).join(' ')}
      data-testid="mini-boss-enemy-strip"
    >
      {safe.map((e) => {
        if (!e) return null;
        const hp = Math.max(0, Number(e.hp || 0));
        const maxHp = Math.max(1, Number(e.maxHp || 1));
        const pct = Math.max(0, Math.min(100, (hp / maxHp) * 100));
        // Diuresis "reserve" (Edward 2026-07-24): HP the diuresis drain will remove
        // from THIS enemy next interaction (even-split share, pre-clamped to hp by
        // the caller), previewed as a blue slab overlaying the right edge of the
        // green fill. Optional/default-0 so every non-HTN roster renders identically.
        const reserveHp = Math.max(0, Number(e.reserveHp || 0));
        const reservePct = Math.min(pct, (Math.min(reserveHp, hp) / maxHp) * 100);
        const clickable = !!e.clickable && typeof onSelect === 'function';
        // Clinical pathophys label shown inline in brackets after the gamified name
        // (minis only; bosses/spawns map to null and stay unannotated).
        const sub = miniSubconcept(e.slug, e.name);
        const handleClick = clickable ? () => onSelect(e) : undefined;
        const handleKeyDown = clickable
          ? (ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') {
                ev.preventDefault();
                onSelect(e);
              }
            }
          : undefined;
        return (
          <TooltipToggleHost
            as="div"
            key={e.id}
            className={[
              'mini-boss-enemy',
              'combat-tt-host',
              // Enemy tooltip = hover only (+ keyboard focus); a tap/click just
              // fires focus-fire targeting, never the tooltip (Edward 2026-07-12).
              'combat-tt-host--hover-only',
              e.isBoss ? 'mini-boss-enemy--boss' : '',
              e.isTarget ? 'mini-boss-enemy--target' : '',
              e.isAttacking ? 'mini-boss-enemy--attacking' : '',
              e.isDead ? 'mini-boss-enemy--dead' : '',
              e.isInvuln ? 'mini-boss-enemy--invuln' : '',
              e.isSpawned ? 'mini-boss-enemy--spawned' : '',
              clickable ? 'mini-boss-enemy--clickable' : '',
            ].filter(Boolean).join(' ')}
            style={e.color ? { '--effect-color': e.color } : undefined}
            data-testid="mini-boss-enemy"
            data-slug={e.slug || undefined}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-pressed={clickable ? !!e.isTarget : undefined}
            aria-label={(clickable ? `Target ${e.name}` : e.name) + (sub ? `, ${sub}` : '')}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
          >
            <div className="mini-boss-enemy-row">
              <span className="mini-boss-enemy-name">
                {e.name}
                {sub ? <span className="mini-boss-enemy-subconcept"> [{sub}]</span> : null}
              </span>
              <span className="mini-boss-enemy-node">{e.tag}</span>
            </div>
            <div
              className="mini-boss-enemy-track"
              aria-label={`${e.name} HP ${hp} of ${maxHp}${reserveHp > 0 ? `, ${Math.round(reserveHp)} reserved by diuresis` : ''}`}
            >
              <div className="mini-boss-enemy-fill" style={{ width: `${pct}%` }} />
              {reservePct > 0 && (
                <div
                  className="mini-boss-enemy-reserve-fill"
                  style={{ left: `${Math.max(0, pct - reservePct)}%`, width: `${reservePct}%` }}
                  aria-hidden="true"
                />
              )}
            </div>
            {/* HP fraction is dropped in embedded previews — redundant with the bar
                fill, and a static preview has no live HP to track (Edward 2026-07-19).
                The track keeps its aria-label, so screen readers still get HP. */}
            {!embedded && <div className="mini-boss-enemy-hp">{hp}/{maxHp}</div>}
            {e.tooltip || null}
          </TooltipToggleHost>
        );
      })}
    </div>
  );
}
